import React from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import {
    EventNote as EventNoteIcon,
    Group as GroupIcon,
    LocationOn as LocationOnIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { StaffSectionHeader } from './StaffDetailSection';

const scheduleCardStyle = {
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    height: '100%',
    boxSizing: 'border-box'
};

const ScheduleField = ({ icon: Icon, label, value }) => (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.4 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: '#e8f5e9', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, mt: 0.2, overflowWrap: 'anywhere' }}>
                {value || 'Not configured'}
            </Typography>
        </Box>
    </Stack>
);

const StaffScheduleOverview = ({ staff, latePolicyName }) => {
    const locations = staff.allowed_locations || [];

    return (
        <Box>
            <StaffSectionHeader
                title="Schedule Overview"
                description="Assigned work pattern, attendance policy, and authorized clocking locations."
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(280px, 0.85fr) minmax(0, 1.4fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={scheduleCardStyle}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                        Work Schedule
                    </Typography>
                    <Stack divider={<Divider flexItem />}>
                        <ScheduleField icon={GroupIcon} label="Shift Group" value={staff.sm_group_name} />
                        <ScheduleField icon={EventNoteIcon} label="Active Pattern" value={staff.sm_pattern_name} />
                        <ScheduleField icon={SecurityIcon} label="Late Policy" value={latePolicyName} />
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ ...scheduleCardStyle, bgcolor: '#f8fafc' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2.5 }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Authorized Clock Locations
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Locations where this employee may record attendance.
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            label={locations.length > 0 ? `${locations.length} location${locations.length === 1 ? '' : 's'}` : 'Unrestricted'}
                            sx={{ bgcolor: locations.length > 0 ? '#dcfce7' : '#e2e8f0', color: locations.length > 0 ? '#166534' : '#475569', fontWeight: 700, flexShrink: 0 }}
                        />
                    </Stack>
                    {locations.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {locations.map(location => (
                                <Chip
                                    key={location}
                                    label={location}
                                    icon={<LocationOnIcon />}
                                    variant="outlined"
                                    sx={{ bgcolor: '#fff', borderColor: '#cbd5e1', color: '#334155', fontWeight: 700, '& .MuiChip-icon': { color: 'primary.main' } }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, borderRadius: 2.5, border: '1px dashed #cbd5e1', bgcolor: '#fff' }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#e8f5e9', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <LocationOnIcon sx={{ fontSize: 19 }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 800 }}>All configured locations are allowed</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>No location restriction is assigned to this employee.</Typography>
                            </Box>
                        </Stack>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default StaffScheduleOverview;
